import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { leaguesApi, League, LeagueType } from '../api/leagues';
import LeagueStatusBadge from '../components/LeagueStatusBadge';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';

function LeagueTypeBadge({ type }: { type?: LeagueType }) {
  const { t } = useTranslation();
  if (type === 'PAAR_LIGA') {
    return <span className="badge border border-green-300 bg-green-50 text-green-700 text-xs">{t('leagueList.paarLigaBadge')}</span>;
  }
  if (type === 'MANNSCHAFTS_LIGA') {
    return <span className="badge border border-orange-300 bg-orange-50 text-orange-700 text-xs">{t('leagueList.mannsLigaBadge')}</span>;
  }
  return null;
}

function leagueTypeImage(type?: LeagueType): string {
  if (type === 'PAAR_LIGA') return '/images/paar.png';
  if (type === 'MANNSCHAFTS_LIGA') return '/images/mannschaft.png';
  return '/images/einzel.png';
}

export default function LeagueList() {
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<LeagueType | ''>('');
  const { t } = useTranslation();

  const STATUS_FILTERS = [
    { value: '', label: t('leagueList.filterAll') },
    { value: 'open', label: t('leagueList.filterOpen') },
    { value: 'active', label: t('leagueList.filterActive') },
    { value: 'knockout', label: t('leagueList.filterKnockout') },
    { value: 'finished', label: t('leagueList.filterFinished') },
  ];

  const TYPE_FILTERS: { value: LeagueType | ''; label: string }[] = [
    { value: '', label: t('leagueList.filterAllTypes') },
    { value: 'PAAR_LIGA', label: t('leagueList.filterPaarLiga') },
    { value: 'MANNSCHAFTS_LIGA', label: t('leagueList.filterMannsLiga') },
  ];

  const { data: leagues = [], isLoading } = useQuery({
    queryKey: ['leagues', statusFilter, typeFilter],
    queryFn: () =>
      leaguesApi
        .list(statusFilter || undefined, typeFilter || undefined)
        .then((r) => r.data),
  });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('leagueList.title')}</h1>

      {/* Status filter */}
      <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === f.value
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Type filter */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {TYPE_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setTypeFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              typeFilter === f.value
                ? f.value === 'PAAR_LIGA'
                  ? 'bg-green-600 text-white'
                  : f.value === 'MANNSCHAFTS_LIGA'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-700 text-white'
                : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-6 bg-gray-200 rounded mb-3 w-1/3" />
              <div className="h-4 bg-gray-100 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : leagues.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <p className="text-lg font-medium">{t('leagueList.notFound')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {leagues.map((league: League) => (
            <Link
              key={league.id}
              to={`/leagues/${league.id}`}
              className="card hover:shadow-lg transition-shadow block group overflow-hidden"
              style={{
                backgroundImage: `linear-gradient(to right, rgba(255,255,255,1) 50%, rgba(255,255,255,0.1) 100%), url(${leagueTypeImage(league.league_type)})`,
                backgroundSize: 'auto 220%',
                backgroundPosition: 'right 35%',
              }}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0" style={{ maxWidth: '50%' }}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate">
                      {league.name}
                    </h3>
                    {league.level && (
                      <span className="badge bg-gray-100 text-gray-600">{league.level}</span>
                    )}
                    <LeagueTypeBadge type={league.league_type} />
                  </div>
                  {league.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{league.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span>
                      {league.confirmed_count ?? 0}{' '}
                      {league.league_type === 'PAAR_LIGA'
                        ? t('leagueList.pairs')
                        : league.league_type === 'MANNSCHAFTS_LIGA'
                        ? t('leagueList.teams')
                        : t('leagueList.players')}{' '}
                      {t('leagueList.confirmedSuffix')}
                    </span>
                    {league.max_players && <span>{t('leagueList.max')}: {league.max_players}</span>}
                    {league.fee !== undefined && league.fee !== null && (
                      <span>{league.fee === 0 ? t('leagueList.free') : `${league.fee} €`}</span>
                    )}
                    {league.registration_deadline && (
                      <span>
                        {t('leagueList.deadline')}: {new Date(league.registration_deadline).toLocaleDateString(getDateLocale())}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <LeagueStatusBadge status={league.status} />
                  {league.is_registered && (
                    <div className="mt-2 text-right">
                      <span className="text-xs text-primary-600 font-medium">{t('leagueList.registered')}</span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
